import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import DashboardLayout from '../../../../components/layout/DashboardLayout';
import EcfBadge, { COMERCIAL_BADGE } from '../components/EcfBadge';
import { DOC_TYPE_LABEL } from '../catalogos';
import {
  ecfService,
  type EcfRecibido,
  type EstadoComercial,
} from '../../../../services/ecf.service';
import { formatMoney } from '../../../../utils/numberFormat';

/**
 * e-CF recibidos: los comprobantes que otros contribuyentes emiten a nuestra empresa.
 *
 * La aprobación comercial es una obligación con plazo: si no se responde, el emisor no sabe si
 * aceptamos la transacción. Por eso la columna marca en rojo las decisiones que se guardaron pero
 * cuyo ACECF no llegó a salir — desde dentro parecen resueltas y para el emisor siguen abiertas.
 *
 * Aprobar o rechazar aquí firma y envía un ACECF de verdad a través de la DGII; no es un cambio
 * de estado local. Ver `backend/src/modules/ecf/inbound/received.service.ts`.
 */

const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'aceptado', label: 'Aprobados' },
  { value: 'rechazado', label: 'Rechazados' },
] as const;

export default function EcfRecibidosPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<EcfRecibido[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [rechazando, setRechazando] = useState<EcfRecibido | null>(null);
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState<EcfRecibido | null>(null);

  const filter = searchParams.get('estado') || 'all';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await ecfService.listReceived(filter === 'all' ? undefined : (filter as EstadoComercial)));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los comprobantes');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (value: string) => {
    if (value === 'all') setSearchParams({}, { replace: true });
    else setSearchParams({ estado: value }, { replace: true });
  };

  /** Reemplaza la fila con lo que devuelve el servidor y refresca el detalle si estaba abierto. */
  const aplicar = (actualizado: EcfRecibido) => {
    setRows((prev) => prev.map((r) => (r.id === actualizado.id ? actualizado : r)));
    setDetalle((prev) => (prev && prev.id === actualizado.id ? actualizado : prev));
  };

  /**
   * El aviso separa las dos mitades del trámite: la decisión siempre queda guardada, pero si el
   * ACECF no salió el emisor todavía no se ha enterado y hay que reintentarlo.
   */
  const avisar = (row: EcfRecibido, actualizado: EcfRecibido, accion: string) => {
    if (actualizado.acecf_status === 'enviado') {
      toast.success(`${row.encf} ${accion} y comunicado al emisor`);
    } else {
      toast.warning(
        `${row.encf} ${accion}, pero la aprobación no llegó a salir: ${
          actualizado.acecf_message ?? 'sin detalle'
        }. Reintenta el envío.`
      );
    }
  };

  const aprobar = async (row: EcfRecibido) => {
    setBusyId(row.id);
    try {
      const actualizado = await ecfService.decideReceived(row.id, 'aceptado');
      aplicar(actualizado);
      avisar(row, actualizado, 'aprobado comercialmente');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo aprobar el comprobante');
    } finally {
      setBusyId(null);
    }
  };

  const confirmarRechazo = async () => {
    if (!rechazando || !motivo.trim()) return;
    const row = rechazando;
    setBusyId(row.id);
    setRechazando(null);
    try {
      const actualizado = await ecfService.decideReceived(row.id, 'rechazado', motivo.trim());
      aplicar(actualizado);
      avisar(row, actualizado, 'rechazado comercialmente');
      setMotivo('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo rechazar el comprobante');
    } finally {
      setBusyId(null);
    }
  };

  const reenviar = async (row: EcfRecibido) => {
    setBusyId(row.id);
    try {
      const actualizado = await ecfService.resendAcecf(row.id);
      aplicar(actualizado);
      if (actualizado.acecf_status === 'enviado') {
        toast.success(`Aprobación comercial de ${row.encf} comunicada al emisor`);
      } else {
        toast.error(actualizado.acecf_message ?? `La aprobación de ${row.encf} sigue sin salir`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo reenviar la aprobación');
    } finally {
      setBusyId(null);
    }
  };

  const descargarXml = async (row: EcfRecibido, tipo: 'ecf' | 'arecf' | 'acecf') => {
    try {
      await ecfService.downloadReceivedXml(row.id, row.encf, tipo);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo descargar el XML');
    }
  };

  const fecha = (iso: string) =>
    new Date(iso).toLocaleString('es-DO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Encabezado */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">e-CF Recibidos</h1>
            <p className="text-gray-600">
              Comprobantes que otros contribuyentes emiten a tu empresa · aprobación comercial
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={load}
              disabled={loading}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-refresh-line mr-2"></i>
              Actualizar
            </button>
            <button
              onClick={() => navigate('/taxes/ecf')}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Volver
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3.5 py-2 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                filter === f.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loadError && (
          <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded-lg flex items-center justify-between gap-4">
            <span>
              <i className="ri-error-warning-line mr-2"></i>
              {loadError}
            </span>
            <button onClick={load} className="text-sm font-medium hover:underline whitespace-nowrap">
              Reintentar
            </button>
          </div>
        )}

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Comprobantes recibidos
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({rows.length} {rows.length === 1 ? 'registro' : 'registros'})
              </span>
            </h3>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      e-NCF
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Emisor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recibido
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aprob. comercial
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((r) => {
                    const comercial =
                      COMERCIAL_BADGE[r.commercial_status] ?? COMERCIAL_BADGE.pendiente;
                    const sinComunicar =
                      r.commercial_status !== 'pendiente' && r.acecf_status !== 'enviado';
                    const busy = busyId === r.id;

                    return (
                      <tr key={r.id} className="hover:bg-gray-50 align-top">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-semibold text-gray-900">
                          {r.encf}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="font-medium">{r.doc_type}</span>
                          <span className="block text-xs text-gray-500">
                            {DOC_TYPE_LABEL[r.doc_type]}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {r.issuer_name ?? '—'}
                          {r.issuer_rnc && (
                            <span className="block text-xs font-mono text-gray-500">
                              RNC {r.issuer_rnc}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatMoney(r.total, 'RD$')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {fecha(r.received_at)}
                          {r.arecf_status === '1' && (
                            <span className="block text-xs text-red-600 mt-1">
                              Acuse: no recibido (cód. {r.arecf_code ?? '—'})
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <EcfBadge variant={comercial.variant}>{comercial.label}</EcfBadge>
                          {r.commercial_reason && (
                            <span className="block text-xs text-gray-500 mt-1 max-w-xs">
                              {r.commercial_reason}
                            </span>
                          )}
                          {sinComunicar && (
                            <span className="block text-xs text-red-600 mt-1">
                              Sin comunicar al emisor
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                          {busy ? (
                            <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                          ) : (
                            <>
                              <button
                                onClick={() => setDetalle(r)}
                                className="text-gray-600 hover:text-gray-900"
                                title="Ver detalle"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                              {r.commercial_status === 'pendiente' && r.arecf_status !== '1' && (
                                <>
                                  <button
                                    onClick={() => aprobar(r)}
                                    className="text-green-600 hover:text-green-900"
                                    title="Aprobar comercialmente"
                                  >
                                    <i className="ri-check-line"></i>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setRechazando(r);
                                      setMotivo('');
                                    }}
                                    className="text-red-600 hover:text-red-900"
                                    title="Rechazar comercialmente"
                                  >
                                    <i className="ri-close-line"></i>
                                  </button>
                                </>
                              )}
                              {sinComunicar && (
                                <button
                                  onClick={() => reenviar(r)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Reenviar la decisión al emisor"
                                >
                                  <i className="ri-refresh-line"></i>
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                        {filter === 'all'
                          ? 'Sin comprobantes recibidos. Aquí aparecen los e-CF que tus proveedores envían a tu empresa.'
                          : 'Sin comprobantes en este estado.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal: rechazo comercial */}
        {rechazando && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Rechazar comprobante</h3>
              <p className="text-sm text-gray-500 mb-4 font-mono">
                {rechazando.encf} · {rechazando.issuer_name ?? rechazando.issuer_rnc}
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo del rechazo
                </label>
                <input
                  type="text"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  maxLength={500}
                  autoFocus
                  placeholder="Ej.: la mercancía nunca se recibió"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sin motivo el emisor recibe un rechazo sin explicación y no puede corregirlo. La
                  DGII también lo exige cuando el estado del ACECF es "rechazada".
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setRechazando(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmarRechazo}
                  disabled={!motivo.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  Rechazar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: detalle */}
        {detalle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 font-mono">{detalle.encf}</h3>
                <button
                  onClick={() => setDetalle(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Emisor</dt>
                  <dd className="text-gray-900 font-medium">{detalle.issuer_name ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">RNC del emisor</dt>
                  <dd className="text-gray-900 font-mono">{detalle.issuer_rnc ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Tipo</dt>
                  <dd className="text-gray-900">
                    {detalle.doc_type} — {DOC_TYPE_LABEL[detalle.doc_type]}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Monto total</dt>
                  <dd className="text-gray-900 font-medium">
                    {formatMoney(detalle.total, 'RD$')}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Fecha de emisión</dt>
                  <dd className="text-gray-900">{detalle.fecha_emision ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Recibido</dt>
                  <dd className="text-gray-900">{fecha(detalle.received_at)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Acuse de recibo enviado</dt>
                  <dd className="text-gray-900">
                    {detalle.arecf_status === '0'
                      ? 'Sí'
                      : `No (cód. ${detalle.arecf_code ?? '—'})`}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Aprobación comercial</dt>
                  <dd>
                    <EcfBadge
                      variant={
                        (COMERCIAL_BADGE[detalle.commercial_status] ?? COMERCIAL_BADGE.pendiente)
                          .variant
                      }
                    >
                      {
                        (COMERCIAL_BADGE[detalle.commercial_status] ?? COMERCIAL_BADGE.pendiente)
                          .label
                      }
                    </EcfBadge>
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">ACECF comunicado</dt>
                  <dd className="text-gray-900">
                    {detalle.acecf_status === 'enviado'
                      ? `Sí${detalle.acecf_at ? ` · ${fecha(detalle.acecf_at)}` : ''}`
                      : 'No'}
                  </dd>
                </div>
              </dl>

              {detalle.commercial_reason && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500">Motivo</p>
                  <p className="text-sm text-gray-900">{detalle.commercial_reason}</p>
                </div>
              )}

              {detalle.acecf_status !== 'enviado' && detalle.acecf_message && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">
                  <p className="text-sm font-medium">La aprobación no llegó al emisor</p>
                  <p className="text-sm mt-1">{detalle.acecf_message}</p>
                </div>
              )}

              {/* Los tres documentos del intercambio: el del emisor, nuestro acuse y nuestra
                  aprobación. Se descargan del servidor y no se guardan en memoria: un e-CF con
                  muchas líneas más su firma pesa decenas de KB por fila. */}
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Documentos XML</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => descargarXml(detalle, 'ecf')}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                  >
                    <i className="ri-file-code-line mr-1"></i>
                    Comprobante del emisor
                  </button>
                  <button
                    onClick={() => descargarXml(detalle, 'arecf')}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                  >
                    <i className="ri-download-line mr-1"></i>
                    Acuse de recibo (ARECF)
                  </button>
                  {detalle.commercial_status !== 'pendiente' && (
                    <button
                      onClick={() => descargarXml(detalle, 'acecf')}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                    >
                      <i className="ri-download-line mr-1"></i>
                      Aprobación comercial (ACECF)
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-6">
                <button
                  onClick={() => setDetalle(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
